import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore, selectHasPermission } from '../../stores/auth'
import { Spinner } from '../../components/ui/Spinner'
import { CarduriSelector } from '../../components/ui/CarduriSelector'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { PanouLateral, SectiunePanou } from '../../components/ui/PanouLateral'
import { CapSortabil, useSortare } from '../../components/ui/Sortare'
import { Explicatie } from '../../components/ui/Explicatie'
import { Pastila } from '../../components/ui/Pastila'
import { ETICHETE_CIFRE } from '@b4l/shared'

/**
 * Fila „Profitabilitate" din Tablou de bord vânzări (02.11.2026): pe fiecare agent și pe fiecare
 * client, vânzările, costul mărfii, serviciile facturate de client și ce rămâne.
 *
 * Victor, adminii și managerii de vânzări (`vanzari.profitabilitate:read`). Macheta aprobată:
 * https://claude.ai/artifact/MD2gDL5WtWYaNqck2eBjdZ — design în
 * `docs/superpowers/specs/2026-11-02-profitabilitate-agenti-design.md`.
 */

type Perioada = 'luna' | 'trimestru' | 'an' | 'ytd'
type SursaCost = 'factura' | 'intrari' | 'catalog' | 'lipsa' | 'reducere'
type TipFactura = 'marketing' | 'bonus' | 'raft' | 'altele'
type SursaPerioada = 'manual' | 'text' | 'regula'

interface Metrici {
  vanzari: number
  cantitate: number
  cost: number
  servicii: number
  profit: number
  marja: number | null
  serviciiPct: number | null
  vanzariFaraCost: number
  deClasificatNr: number
  deClasificatSuma: number
  surseCost: Record<SursaCost, number>
}
interface Costuri {
  salariuTaxe: number
  bonus: number
  auto: number
  carburant: number
  total: number
  masini: string[]
  luniLipsa: string[]
}
/** `faraCosturi` = niciun salariu completat: profitul după agenți nu e comparabil. */
type StareCosturi = 'completa' | 'estimata' | 'faraCosturi'
type SumeCosturi = Pick<Costuri, 'salariuTaxe' | 'bonus' | 'auto' | 'carburant' | 'total'>
interface CosturiTotal extends SumeCosturi {
  luniLipsa: string[]
  agentiIncompleti: number
  agenti: number
  stare: StareCosturi
}
interface Agent extends Metrici {
  cheie: string
  nume: string
  national: boolean
  clienti: number
  subZero: number
  vanzariAnTrecut: number | null
  profitAnTrecut: number | null
  comparatieCostEstimat: boolean
  /** Salariu + taxe, bonus, mașină, carburant pe perioadă. `null` la conturile naționale. */
  costuri: Costuri | null
  dupaCosturi: number | null
  marjaDupaCosturi: number | null
  /** Profitul conturilor nationale, doar la agentul care raspunde de ele. */
  profitNationale: number | null
  stareCosturi: StareCosturi | null
  dupaCosturiAnTrecut: number | null
  stareCosturiAnTrecut: StareCosturi | null
}
interface LunaCost {
  luna: string
  salariuTaxe: number | null
  bonus: number | null
  auto: number | null
  carburant: number | null
  carburantFillGo: number
}
interface Client extends Metrici {
  cheie: string
  agentCheie: string
  id: number | null
  nume: string
  national: boolean
  vanzariAnTrecut: number | null
  profitAnTrecut: number | null
  comparatieCostEstimat: boolean
}
interface Nelegate {
  numar: number
  suma: number
  faraCifInCrm: number
  valuta: number
  faraTva: number
}
interface Raport {
  /** Agentul care raspunde de conturile nationale (de ex. Victor Ciortan), daca e unul singur. */
  responsabilNationale: string | null
  interval: { de: string; pana: string }
  eticheta: string
  etichetaComparatie: string
  inCurs: boolean
  provizoriu: boolean
  pragMarja: number
  pragServicii: number
  total: Metrici & {
    clienti: number
    subZero: number
    vanzariAnTrecut: number
    profitAnTrecut: number
    comparatieCostEstimat: boolean
    costuriAgenti: CosturiTotal
    profitDupaAgenti: number
    marjaDupaAgenti: number | null
    luniLipsa: string[]
    costuriAgentiAnTrecut: CosturiTotal
    profitDupaAgentiAnTrecut: number
  }
  nelegate: Nelegate
  agenti: Agent[]
  clienti: Client[]
}
interface Detaliu {
  produse: {
    sku: string
    denumire: string
    cantitate: number
    vanzari: number
    cost: number | null
    vanzariFaraCost: number
    surse: SursaCost[]
  }[]
  facturi: { numar: string; data: string; valoare: number; storno: boolean }[]
  facturiPrimite: {
    id: number
    numar: string | null
    data: string | null
    emitent: string | null
    /** Partea din perioada aleasa. */
    valoare: number
    valoareTotala: number
    luni: string[]
    sursaPerioada: SursaPerioada
    sursaTip: 'manual' | 'automat' | null
    tip: TipFactura | null
  }[]
}

const PERIOADE: [Perioada, string][] = [
  ['luna', 'Lună'],
  ['trimestru', 'Trimestru'],
  ['an', 'An'],
  ['ytd', 'De la 1 ian.'],
]
const ETICHETA_SURSA: Record<SursaCost, string> = {
  factura: 'costul de pe factura din ExpertAccounts',
  intrari: 'ultimul preț de intrare (Intrări în gestiune)',
  catalog: 'prețul de intrare din catalogul ExpertAccounts',
  lipsa: 'fără cost',
  reducere: 'notă de reducere, fără marfă — n-are cost',
}
const ETICHETA_TIP: Record<TipFactura, string> = {
  marketing: 'Marketing',
  bonus: 'Bonus de volum',
  raft: 'Raft / listare',
  altele: 'Altele (nu scade profitul)',
}

const lei = (n: number) => n.toLocaleString('ro-RO', { maximumFractionDigits: 0 })
const pct = (n: number | null) =>
  n == null
    ? '—'
    : `${n.toLocaleString('ro-RO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
const dmy = (s: string | null) => (s ? s.split('-').reverse().join('.') : '—')

function Semnat({ n, className = '' }: { n: number; className?: string }) {
  // Pierderea: roșu temperat DOAR pe cifră, cu semn minus — nu tot rândul.
  return (
    <span className={`tabular-nums ${n < 0 ? 'text-b4l-red-dark' : ''} ${className}`}>
      {n < 0 ? '−' : ''}
      {lei(Math.abs(n))}
    </span>
  )
}

function Variatie({ acum, trecut }: { acum: number; trecut: number | null }) {
  if (trecut == null || trecut === 0)
    return <span className="text-b4l-text-dim">{acum ? 'nou' : '—'}</span>
  const d = ((acum - trecut) / Math.abs(trecut)) * 100
  return (
    <Explicatie text={`Aceeași perioadă de anul trecut: ${lei(trecut)} lei.`}>
      <span className={d >= 0 ? 'text-b4l-green-dark' : 'text-b4l-red-dark'}>
        {d >= 0 ? '+' : '−'}
        {Math.abs(d).toFixed(0)}%
      </span>
    </Explicatie>
  )
}

/** „Conturi naționale · Victor Ciortan" pentru grupul lanturilor, numele simplu in rest. */
function numeAgent(a: { nume: string; national: boolean }, r: Raport): string {
  return a.national && r.responsabilNationale ? `${a.nume} · ${r.responsabilNationale}` : a.nume
}

/**
 * Calculul pas cu pas: vânzări − cost marfă − servicii client = profit; cu costurile agenților,
 * mai departe: − salariu + taxe − bonus − mașină − carburant = profit după agenți. `restrans`
 * (cardul de sus) le arată ca o singură treaptă, care se desface pe cele patru.
 */
function Cascada({
  m,
  costuri,
  nationale,
  restrans = false,
  etichetaBrut = '= Profit din clienți',
  etichetaFinal = '= Rămâne după agent (estimat)',
}: {
  m: Metrici
  costuri?: SumeCosturi | null
  /** Profitul conturilor nationale, la agentul care raspunde de ele. */
  nationale?: number | null
  restrans?: boolean
  etichetaBrut?: string
  etichetaFinal?: string
}) {
  const [desfacut, setDesfacut] = useState(false)
  const max = Math.max(
    Math.abs(m.vanzari),
    Math.abs(m.cost) + Math.abs(m.servicii),
    Math.abs(m.profit + (nationale ?? 0)),
    1,
  )
  const ramas = costuri ? m.profit + (nationale ?? 0) - costuri.total : 0
  const rand = (eticheta: ReactNode, v: number, culoare: string, minus = false) => (
    <div className="grid grid-cols-[130px_1fr_90px] items-center gap-2 text-[13px]">
      <span className="text-b4l-text-muted">{eticheta}</span>
      <div className="h-3 rounded bg-b4l-bg-soft">
        <div
          className={`h-3 rounded ${culoare}`}
          style={{ width: `${Math.min(1, Math.abs(v) / max) * 100}%` }}
        />
      </div>
      <span className="text-right tabular-nums">
        {minus && v !== 0 ? '−' : ''}
        <Semnat n={minus ? Math.abs(v) : v} />
      </span>
    </div>
  )
  const componente = costuri && (
    <>
      {rand('− Salariu + taxe', costuri.salariuTaxe, 'bg-b4l-red/30', true)}
      {rand('− Bonus', costuri.bonus, 'bg-b4l-red/30', true)}
      {rand('− Mașină', costuri.auto, 'bg-b4l-red/30', true)}
      {rand('− Carburant', costuri.carburant, 'bg-b4l-red/30', true)}
    </>
  )
  return (
    <div className="grid gap-1.5">
      {rand('Vânzări nete', m.vanzari, 'bg-b4l-blue/40')}
      {rand('− Cost marfă', m.cost, 'bg-b4l-red/30', true)}
      {rand('− Servicii client', m.servicii, 'bg-b4l-red/30', true)}
      {rand(
        costuri ? etichetaBrut : '= Profit',
        m.profit,
        m.profit < 0 ? 'bg-b4l-red/50' : 'bg-b4l-blue',
      )}
      {nationale != null && (
        <>
          {rand(
            '+ Conturi naționale',
            nationale,
            nationale < 0 ? 'bg-b4l-red/50' : 'bg-b4l-blue/70',
          )}
          {rand(
            '= Profit total',
            m.profit + nationale,
            m.profit + nationale < 0 ? 'bg-b4l-red/50' : 'bg-b4l-blue',
          )}
        </>
      )}
      {costuri && (
        <>
          {restrans ? (
            <>
              {rand(
                <button
                  type="button"
                  aria-expanded={desfacut}
                  onClick={() => setDesfacut((d) => !d)}
                  className="inline-flex items-center gap-0.5 text-left text-b4l-blue-dark hover:underline"
                >
                  {desfacut ? <ChevronDown size={13} /> : <ChevronRight size={13} />}− Costuri
                  agenți
                </button>,
                costuri.total,
                'bg-b4l-red/30',
                true,
              )}
              {desfacut && <div className="grid gap-1.5 pl-3">{componente}</div>}
            </>
          ) : (
            componente
          )}
          {rand(etichetaFinal, ramas, ramas < 0 ? 'bg-b4l-red/50' : 'bg-b4l-green')}
        </>
      )}
    </div>
  )
}

const LUNI_SCURT = [
  'ian.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'iun.',
  'iul.',
  'aug.',
  'sep.',
  'oct.',
  'nov.',
  'dec.',
]
const etLuna = (l: string) => `${LUNI_SCURT[Number(l.slice(5, 7)) - 1]} ${l.slice(0, 4)}`

const ETICHETA_SURSA_PERIOADA: Record<SursaPerioada, string> = {
  manual: 'corectată',
  text: 'citită din factură',
  regula: 'după data emiterii',
}
const EXPLICATIE_SURSA_PERIOADA: Record<SursaPerioada, string> = {
  manual: 'Perioada a fost corectată de mână.',
  text: 'Perioada e scrisă pe factură (în notă sau în descrierea serviciului).',
  regula:
    'Factura nu spune perioada. Emisă în ultima zi lucrătoare a lunii → luna emiterii; altfel → luna dinainte.',
}
const lunaUrmatoare = (l: string) => {
  const [y, m] = l.split('-').map(Number)
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`
}
/** „sep. 2026", „ian. 2026 – dec. 2026" sau „feb. 2026, iun. 2026". */
function textLuni(luni: string[]): string {
  if (!luni.length) return '—'
  if (luni.length === 1) return etLuna(luni[0])
  const legate = luni.every((l, i) => i === 0 || lunaUrmatoare(luni[i - 1]) === l)
  return legate
    ? `${etLuna(luni[0])} – ${etLuna(luni[luni.length - 1])}`
    : luni.map(etLuna).join(', ')
}

const LUNA_RE = /^\d{4}-\d{2}$/

/** Tipul facturii primite propus de program din textul ei, neconfirmat de nimeni. */
function PastilaAutomat() {
  return (
    <Explicatie text="Propus de program din textul facturii: servicii (e-learning, ads, bannere, prestări servicii) scad profitul; marfă și vouchere → Altele. Alegerea ta îl înlocuiește.">
      <Pastila ton="gri">propus automat</Pastila>
    </Explicatie>
  )
}

/** Perioada la care se refera o factura primita, cu corectarea ei. */
function PerioadaFacturii({
  id,
  luni,
  sursa,
  poateSchimba,
  ocupat,
  onSalveaza,
}: {
  id: number
  luni: string[]
  sursa: SursaPerioada
  poateSchimba: boolean
  ocupat: boolean
  onSalveaza: (perioada: { de: string; pana: string } | null) => void
}) {
  const [editez, setEditez] = useState(false)
  const [de, setDe] = useState(luni[0] ?? '')
  const [pana, setPana] = useState(luni[luni.length - 1] ?? '')
  if (!editez)
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-b4l-text-muted">Pentru:</span>
        <span>{textLuni(luni)}</span>
        <Explicatie text={EXPLICATIE_SURSA_PERIOADA[sursa]}>
          <Pastila ton={sursa === 'regula' ? 'galben' : 'gri'}>
            {ETICHETA_SURSA_PERIOADA[sursa]}
          </Pastila>
        </Explicatie>
        {poateSchimba && (
          <button
            type="button"
            className="text-b4l-blue underline-offset-2 hover:underline"
            onClick={() => {
              setDe(luni[0] ?? '')
              setPana(luni[luni.length - 1] ?? '')
              setEditez(true)
            }}
          >
            Schimbă
          </button>
        )}
      </div>
    )
  const valid = LUNA_RE.test(de) && LUNA_RE.test(pana) && de <= pana
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1">
        <span className="text-b4l-text-muted">De la</span>
        <input
          id={`perioada-de-${id}`}
          type="month"
          value={de}
          onChange={(e) => setDe(e.target.value)}
          className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1"
        />
      </label>
      <label className="flex items-center gap-1">
        <span className="text-b4l-text-muted">până la</span>
        <input
          id={`perioada-pana-${id}`}
          type="month"
          value={pana}
          onChange={(e) => setPana(e.target.value)}
          className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1"
        />
      </label>
      <button
        type="button"
        disabled={!valid || ocupat}
        className="rounded-md bg-b4l-blue px-2 py-1 text-white disabled:opacity-50"
        onClick={() => {
          onSalveaza({ de, pana })
          setEditez(false)
        }}
      >
        Salvează
      </button>
      {sursa === 'manual' && (
        <button
          type="button"
          disabled={ocupat}
          className="rounded-md border border-b4l-border px-2 py-1"
          onClick={() => {
            onSalveaza(null)
            setEditez(false)
          }}
        >
          Înapoi la automat
        </button>
      )}
      <button
        type="button"
        className="px-2 py-1 text-b4l-text-muted"
        onClick={() => setEditez(false)}
      >
        Renunță
      </button>
      {!valid && (
        <span className="text-b4l-red-dark">Prima lună trebuie să fie înaintea ultimei.</span>
      )}
    </div>
  )
}

/** Avertismentele unui rând: cost lipsă, facturi de clasificat. */
function Semne({ m, prag }: { m: Metrici; prag: number }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {m.vanzariFaraCost !== 0 && (
        <Explicatie
          text={`Pentru ${lei(m.vanzariFaraCost)} lei din vânzări n-am găsit costul de intrare. Profitul nu-l scade, deci poate fi mai mare decât în realitate cu cel mult atât.`}
        >
          <Pastila ton="galben">cost lipsă</Pastila>
        </Explicatie>
      )}
      {m.deClasificatNr > 0 && (
        <Explicatie
          text={`${m.deClasificatNr} ${m.deClasificatNr === 1 ? 'factură primită' : 'facturi primite'} de la client (${lei(m.deClasificatSuma)} lei) încă neclasificate: nu scad profitul până nu spune cineva ce sunt.`}
        >
          <Pastila ton="mov">de clasificat</Pastila>
        </Explicatie>
      )}
      {m.serviciiPct != null && m.serviciiPct > prag && (
        <Explicatie
          text={`Serviciile facturate de client sunt ${pct(m.serviciiPct)} din vânzări. Peste ${prag}% merită o discuție cu contabilitatea despre contract.`}
        >
          <Pastila ton="galben">servicii {pct(m.serviciiPct)}</Pastila>
        </Explicatie>
      )}
    </span>
  )
}

type Card = 'vanzari' | 'profit' | 'marja' | 'sub'
type ChipAgenti = 'toti' | 'slab' | 'cusub' | 'lipsa' | 'clasif'
type ChipClienti = 'toti' | 'sub' | 'slab' | 'serv' | 'lipsa' | 'clasif'
type ColAgent =
  | 'nume'
  | 'vanzari'
  | 'cantitate'
  | 'cost'
  | 'servicii'
  | 'profit'
  | 'dupaCosturi'
  | 'marja'
  | 'clienti'
  | 'var'
type ColClient = 'nume' | 'vanzari' | 'cantitate' | 'cost' | 'servicii' | 'profit' | 'marja' | 'var'

/**
 * Cat asteapta pagina raportul. Un an calculat prima data dureaza ~2 minute (liniile tuturor
 * facturilor din EA); apoi vine din cache. Implicitul de 15 s al `api` il taia mereu.
 */
const TIMP_CALCUL = 180_000

/** Cat de departe in urma se poate merge (perioade); acelasi prag ca in API. */
const PASI_INAPOI = 36
const LUNI_LUNG = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
]

/** Lista din care se alege perioada: [pas, eticheta], de la cea curenta inapoi. */
function optiuniPerioada(p: Perioada): [number, string][] {
  const azi = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' })
  const [ay, am] = azi.split('-').map(Number)
  const out: [number, string][] = []
  for (let k = 0; k >= -PASI_INAPOI; k--) {
    if (p === 'luna') {
      const t = (ay * 12 + am - 1 + k) as number
      out.push([k, `${LUNI_LUNG[((t % 12) + 12) % 12]} ${Math.floor(t / 12)}`])
    } else if (p === 'trimestru') {
      const q0 = ay * 4 + Math.floor((am - 1) / 3) + k
      out.push([k, `T${(((q0 % 4) + 4) % 4) + 1} ${Math.floor(q0 / 4)}`])
    } else if (p === 'an') {
      out.push([k, String(ay + k)])
    } else {
      out.push([k, k === 0 ? `De la 1 ian. ${ay}` : `De la 1 ian. ${ay + k} (aceleași zile)`])
    }
    if ((p === 'an' || p === 'ytd') && k <= -5) break
  }
  return out
}

export function FilaProfitabilitate() {
  const [perioada, setPerioada] = useState<Perioada>('luna')
  const [pas, setPas] = useState(0)
  const [card, setCard] = useState<Card>('profit')
  const [agentDeschis, setAgentDeschis] = useState<string | null>(null)
  const [chipA, setChipA] = useState<ChipAgenti>('toti')
  const [chipC, setChipC] = useState<ChipClienti>('toti')
  const [panouAgent, setPanouAgent] = useState<string | null>(null)
  const [panouClient, setPanouClient] = useState<string | null>(null)

  const q = useQuery<Raport>({
    queryKey: ['profitabilitate', perioada, pas],
    queryFn: async () =>
      (
        await api.get<Raport>('/profitabilitate', {
          params: { perioada, pas },
          timeout: TIMP_CALCUL,
        })
      ).data,
  })
  const r = q.data

  const agenti = r?.agenti ?? []
  const agent = agentDeschis ? (agenti.find((a) => a.cheie === agentDeschis) ?? null) : null
  const clientiAgent = useMemo(
    () => (r && agentDeschis ? r.clienti.filter((c) => c.agentCheie === agentDeschis) : []),
    [r, agentDeschis],
  )

  const pa = r ? profitAfisat(r, agent) : null
  const cmp = r && pa ? comparatieProfit(r, agent ?? r.total, pa) : null

  const alegePerioada = (p: Perioada) => {
    setPerioada(p)
    setPas(0)
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg bg-b4l-bg-soft p-0.5" role="group" aria-label="Perioada">
          {PERIOADE.map(([k, e]) => (
            <button
              key={k}
              type="button"
              aria-pressed={perioada === k}
              onClick={() => alegePerioada(k)}
              className={`rounded-md px-3 py-1 text-sm ${
                perioada === k ? 'bg-b4l-surface font-semibold shadow-sm' : 'text-b4l-text-muted'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 font-semibold">
          <button
            type="button"
            aria-label="Perioada anterioară"
            disabled={pas <= -PASI_INAPOI}
            onClick={() => setPas((p) => Math.max(-PASI_INAPOI, p - 1))}
            className="rounded-md border border-b4l-border p-1 hover:bg-b4l-bg-soft"
          >
            <ChevronLeft size={16} />
          </button>
          <select
            id="profitabilitate-perioada"
            aria-label="Alege perioada"
            value={pas}
            onChange={(e) => setPas(Number(e.target.value))}
            className="min-w-[9rem] cursor-pointer rounded-md border border-transparent bg-transparent px-1 py-0.5 text-center font-semibold hover:border-b4l-border"
          >
            {optiuniPerioada(perioada).map(([k, e]) => (
              <option key={k} value={k}>
                {e}
              </option>
            ))}
          </select>
          <button
            type="button"
            aria-label="Perioada următoare"
            disabled={pas >= 0}
            onClick={() => setPas((p) => Math.min(0, p + 1))}
            className="rounded-md border border-b4l-border p-1 hover:bg-b4l-bg-soft disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        {r && <span className="text-sm text-b4l-text-muted">față de {r.etichetaComparatie}</span>}
        {r?.provizoriu && (
          <Explicatie text="Facturile primite de la clienți pentru perioada asta (marketing, bonusuri) vin de obicei până pe ~25 ale lunii următoare. Până atunci profitul poate scădea.">
            <Pastila ton="galben">provizoriu</Pastila>
          </Explicatie>
        )}
        {r && r.nelegate.numar > 0 && (
          <Explicatie
            text={[
              'Facturi primite de la clienți care NU intră în profit:',
              r.nelegate.faraCifInCrm
                ? `${r.nelegate.faraCifInCrm} de la un client din CRM fără codul fiscal al facturii (completează codul fiscal pe client)`
                : '',
              r.nelegate.valuta ? `${r.nelegate.valuta} în valută (n-avem curs)` : '',
              r.nelegate.faraTva
                ? `${r.nelegate.faraTva} fără TVA cunoscut (nu ghicim valoarea)`
                : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <Pastila ton="mov">
              facturi primite nelegate: {r.nelegate.numar}
              {r.nelegate.suma ? `, ${lei(r.nelegate.suma)} lei` : ''}
            </Pastila>
          </Explicatie>
        )}
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-sm text-b4l-text-muted">
          <Spinner />
          Se calculează… Prima dată pentru o perioadă lungă (un an) poate dura până la 2–3 minute;
          apoi se deschide pe loc.
        </div>
      )}
      {q.isError && (
        <p className="text-sm text-b4l-red-dark">Nu s-au putut citi cifrele. Reîncarcă pagina.</p>
      )}

      {r && (
        <>
          <nav
            className="flex flex-wrap items-center gap-1.5 text-sm text-b4l-text-muted"
            aria-label="Unde ești"
          >
            {agent ? (
              <>
                <button
                  type="button"
                  className="text-b4l-blue-dark hover:underline"
                  onClick={() => {
                    setAgentDeschis(null)
                    setChipC('toti')
                  }}
                >
                  Toți agenții
                </button>
                <span>›</span>
                <span className="text-b4l-text">{agent.nume}</span>
              </>
            ) : (
              <span>Toți agenții (fără conturile naționale, care au grupul lor)</span>
            )}
          </nav>

          <Carduri r={r} m={agent ?? r.total} p={pa!} cmp={cmp!} card={card} setCard={setCard} />
          <DetaliuCard
            card={card}
            m={agent ?? r.total}
            p={pa!}
            cmp={cmp!}
            randuri={agent ? clientiAgent : agenti.filter((a) => !a.national)}
            prag={r.pragMarja}
          />

          {agent ? (
            <TabelClienti
              r={r}
              clienti={clientiAgent}
              chip={chipC}
              setChip={setChipC}
              onDeschide={setPanouClient}
            />
          ) : (
            <TabelAgenti r={r} chip={chipA} setChip={setChipA} onDeschide={setPanouAgent} />
          )}

          <p className="max-w-[90ch] text-xs text-b4l-text-dim">
            Toate sumele sunt fără TVA. Vânzări = facturile emise de noi către clienții B2B, după
            reducerile de pe factură și stornouri. Cost marfă = prețul de intrare din ExpertAccounts
            pe fiecare linie vândută. Servicii client = facturile primite de la același client (cod
            fiscal), clasificate ca marketing, bonus sau raft. Profit după agenți = profitul minus
            costurile agenților (salariu + taxe, bonus, mașină, carburant): o contribuție, nu
            profitul net. Nu intră: transportul, mostrele, comisionul agentului (dacă nu e plătit ca
            bonus în statul de plată), cheltuielile generale ale firmei, impozitul.
          </p>
        </>
      )}

      {r && panouAgent && (
        <PanouAgent
          r={r}
          cheie={panouAgent}
          onInchide={() => setPanouAgent(null)}
          onClienti={(k) => {
            setPanouAgent(null)
            setAgentDeschis(k)
            setChipC('toti')
          }}
        />
      )}
      {r && panouClient && (
        <PanouClient
          r={r}
          cheie={panouClient}
          perioada={perioada}
          pas={pas}
          onInchide={() => setPanouClient(null)}
        />
      )}
    </div>
  )
}

/**
 * Ce arată cardul „Profit": profitul după costurile agenților (Mădălin, 03.11.2026), la toți
 * agenții sau la agentul deschis. Grupul conturilor naționale n-are agent de teren: acolo rămâne
 * profitul simplu.
 */
interface ProfitAfisat {
  valoare: number
  marja: number | null
  /** `null` = fără costuri de agent (conturile naționale). */
  costuri: SumeCosturi | null
  nationale: number | null
  /** Explicația steluței: ce costuri lipsesc. `null` = complete. */
  incomplet: string | null
  stare: StareCosturi | null
  /** Aceeași socoteală anul trecut. */
  trecut: number | null
  stareTrecut: StareCosturi | null
  luniLipsaTrecut: string[]
}

const textLipsa = (luni: string[]) => luni.map(etLuna).join(', ')

function profitAfisat(r: Raport, a: Agent | null): ProfitAfisat {
  if (!a) {
    const t = r.total
    const c = t.costuriAgenti
    return {
      valoare: t.profitDupaAgenti,
      marja: t.marjaDupaAgenti,
      costuri: c,
      nationale: null,
      incomplet: t.luniLipsa.length
        ? `Costuri agent incomplete: ${c.agentiIncompleti} din ${c.agenti} agenți n-au salariul completat pe ${textLipsa(t.luniLipsa)}. Profitul după agenți e mai mare decât în realitate până se completează.`
        : null,
      stare: c.stare,
      trecut: t.profitDupaAgentiAnTrecut,
      stareTrecut: t.costuriAgentiAnTrecut.stare,
      luniLipsaTrecut: t.costuriAgentiAnTrecut.luniLipsa,
    }
  }
  if (!a.costuri || a.dupaCosturi == null)
    return {
      valoare: a.profit,
      marja: a.marja,
      costuri: null,
      nationale: null,
      incomplet: null,
      stare: null,
      trecut: a.profitAnTrecut,
      stareTrecut: null,
      luniLipsaTrecut: [],
    }
  return {
    valoare: a.dupaCosturi,
    marja: a.marjaDupaCosturi,
    costuri: a.costuri,
    nationale: a.profitNationale,
    incomplet: a.costuri.luniLipsa.length
      ? `Costuri agent incomplete: fără salariu completat pe ${textLipsa(a.costuri.luniLipsa)}.`
      : null,
    stare: a.stareCosturi,
    trecut: a.dupaCosturiAnTrecut,
    stareTrecut: a.stareCosturiAnTrecut,
    luniLipsaTrecut: [],
  }
}

/** Steluța de costuri incomplete, ca în coloana „După costuri agent". */
function Steluta() {
  return (
    <span className="text-b4l-amber-dark" aria-label="costuri incomplete">
      *
    </span>
  )
}

/** Rândul de comparație de sub cardul „Profit": textul scurt și de ce e făcut așa. */
interface ComparatieProfit {
  scurt: string
  explicatie: string | null
}

/**
 * Comparația cu anul trecut se face pe aceeași bază. Profitul după agenți se compară doar dacă
 * ambele perioade sunt încheiate și au salarii completate; altfel se compară profitul înainte de
 * agenți (fără niciun salariu completat, ar scădea doar carburantul; pe o perioadă în curs,
 * costurile sunt pe luni întregi, dar vânzările doar până azi).
 */
function comparatieProfit(
  r: Raport,
  m: Metrici & { profitAnTrecut?: number | null; comparatieCostEstimat?: boolean },
  p: ProfitAfisat,
): ComparatieProfit {
  const baza = (v: number | null | undefined) =>
    v == null ? 'fără comparație' : `față de ${lei(v)} lei`
  const costEstimat = m.comparatieCostEstimat
    ? `Pentru ${r.etichetaComparatie} o parte din costul mărfii nu vine de pe facturi (intrări în gestiune, catalog sau lipsă): profitul de atunci e estimat.`
    : null
  const brut = (motiv: string): ComparatieProfit => ({
    scurt: `înainte de agenți: ${lei(m.profit)} ${baza(m.profitAnTrecut)}`,
    explicatie: [`${motiv} Comparăm profitul înainte de agenți cu cel de atunci.`, costEstimat]
      .filter(Boolean)
      .join(' '),
  })
  if (!p.costuri)
    return costEstimat
      ? { scurt: `${baza(m.profitAnTrecut)} (cost estimat)`, explicatie: costEstimat }
      : { scurt: baza(m.profitAnTrecut), explicatie: null }
  if (m.profitAnTrecut == null || p.trecut == null)
    return {
      scurt: 'fără comparație',
      explicatie: `În ${r.etichetaComparatie} n-au fost vânzări: nu e cu ce compara.`,
    }
  if (r.inCurs)
    return brut(
      'Perioada nu s-a încheiat: costurile agenților sunt pe luni întregi, iar vânzările doar până azi, deci profitul după agenți nu se poate compara.',
    )
  if (p.stareTrecut === 'faraCosturi')
    return brut(
      `Pentru ${r.etichetaComparatie} nu sunt completate salariile agenților, deci profitul după agenți de atunci nu se poate compara.`,
    )
  if (p.stare === 'faraCosturi')
    return brut(
      `Pentru ${r.eticheta} nu sunt completate încă salariile agenților, deci profitul după agenți nu se poate compara.`,
    )
  const motive = [
    p.stare === 'estimata'
      ? 'Acum unii agenți n-au salariul completat pe toate lunile: profitul după agenți e mai mare decât în realitate.'
      : null,
    p.stareTrecut === 'estimata'
      ? `Pentru ${r.etichetaComparatie} unii agenți n-au salariul completat${p.luniLipsaTrecut.length ? ` (${textLipsa(p.luniLipsaTrecut)})` : ''}: profitul după agenți de atunci e mai mare decât în realitate.`
      : null,
    costEstimat,
  ].filter(Boolean)
  return motive.length
    ? { scurt: `${baza(p.trecut)} (estimat)`, explicatie: motive.join(' ') }
    : { scurt: baza(p.trecut), explicatie: null }
}

function Carduri({
  r,
  m,
  p,
  cmp,
  card,
  setCard,
}: {
  r: Raport
  m: Metrici & {
    subZero?: number
    clienti?: number
    vanzariAnTrecut?: number | null
    profitAnTrecut?: number | null
    comparatieCostEstimat?: boolean
  }
  p: ProfitAfisat
  cmp: ComparatieProfit
  card: Card
  setCard: (c: Card) => void
}) {
  const baza = (v: number | null | undefined) =>
    v == null ? 'fără comparație' : `față de ${lei(v)} lei`
  return (
    <CarduriSelector<Card>
      eticheta="Ce vrei să vezi"
      ales={card}
      onAlege={setCard}
      carduri={[
        {
          cheie: 'vanzari',
          eticheta: 'Vânzări nete',
          valoare: `${lei(m.vanzari)} lei`,
          detaliu: (
            <>
              <span className="block">{ETICHETE_CIFRE.vanzariNete.detaliu}</span>
              {baza(m.vanzariAnTrecut)}
            </>
          ),
          ton: 'albastru',
        },
        {
          cheie: 'profit',
          eticheta: p.costuri ? ETICHETE_CIFRE.profitDupaAgenti.eticheta : 'Profit',
          // Explicatiile nu sunt focalizabile in cardul-buton; textul lor se repeta in detaliu.
          valoare: (
            <span className="inline-flex items-center gap-1">
              <Semnat n={p.valoare} /> lei
              {p.incomplet && (
                <Explicatie text={p.incomplet} focalizabil={false}>
                  <Steluta />
                </Explicatie>
              )}
            </span>
          ),
          detaliu: (
            <>
              {p.costuri && (
                <span className="block">{ETICHETE_CIFRE.profitDupaAgenti.detaliu}</span>
              )}
              {cmp.explicatie ? (
                <Explicatie text={cmp.explicatie} focalizabil={false}>
                  {cmp.scurt}
                </Explicatie>
              ) : (
                cmp.scurt
              )}
            </>
          ),
          ton: p.valoare < 0 ? 'rosu' : 'verde',
        },
        {
          cheie: 'marja',
          eticheta: p.costuri ? 'Marjă după agenți' : 'Marjă',
          valoare: pct(p.marja),
          detaliu: p.costuri
            ? `înainte de agenți ${pct(m.marja)} · prag sub ${r.pragMarja}%`
            : `prag de atenție: sub ${r.pragMarja}%`,
          ton:
            p.marja != null && p.marja < 0
              ? 'rosu'
              : m.marja != null && m.marja < r.pragMarja
                ? 'galben'
                : 'gri',
        },
        {
          cheie: 'sub',
          eticheta: 'Clienți sub zero',
          valoare: String(m.subZero ?? 0),
          detaliu: `din ${m.clienti ?? 0} clienți activi`,
          ton: (m.subZero ?? 0) > 0 ? 'rosu' : 'gri',
        },
      ]}
    />
  )
}

function DetaliuCard({
  card,
  m,
  p,
  cmp,
  randuri,
  prag,
}: {
  card: Card
  m: Metrici
  p: ProfitAfisat
  cmp: ComparatieProfit
  randuri: (Metrici & { cheie: string; nume: string; vanzariAnTrecut: number | null })[]
  prag: number
}) {
  const cadru = (titlu: string, continut: ReactNode, nota?: string) => (
    <section className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4">
      <h3 className="text-sm font-semibold">{titlu}</h3>
      {continut}
      {nota && <p className="max-w-[80ch] text-xs text-b4l-text-muted">{nota}</p>}
    </section>
  )
  if (card === 'profit')
    return cadru(
      'Cum se ajunge la profit',
      <>
        <Cascada
          m={m}
          costuri={p.costuri}
          nationale={p.nationale}
          restrans
          etichetaBrut="= Profit înainte de agenți"
          etichetaFinal={`= Profit după agenți (${ETICHETE_CIFRE.costuriAgent.detaliu})`}
        />
        {p.incomplet && (
          <p className="text-xs text-b4l-amber-dark">
            <Steluta /> {p.incomplet}
          </p>
        )}
        {cmp.explicatie && (
          <p className="text-xs text-b4l-text-muted">
            Față de anul trecut ({cmp.scurt}): {cmp.explicatie}
          </p>
        )}
      </>,
      p.costuri
        ? 'Profitul după agenți e o contribuție: ce rămâne din vânzări după marfă, serviciile clienților și costurile agenților, înainte de cheltuielile generale ale firmei și de impozit. Nu e profitul net.'
        : 'Fiecare client are aceeași cascadă în panoul lui, cu documentele din spatele fiecărei trepte.',
    )
  const sortate = [...randuri].sort((a, b) => b.vanzari - a.vanzari).slice(0, 12)
  if (card === 'vanzari') {
    const max = Math.max(1, ...sortate.map((x) => Math.max(x.vanzari, x.vanzariAnTrecut ?? 0)))
    return cadru(
      'Vânzări nete față de anul trecut',
      <div className="grid gap-1.5">
        {sortate.map((x) => (
          <div
            key={x.cheie}
            className="grid grid-cols-[minmax(0,180px)_1fr_90px] items-center gap-2 text-[13px]"
          >
            <span className="truncate">{x.nume}</span>
            <div className="grid gap-0.5">
              <div
                className="h-2.5 rounded bg-b4l-blue/60"
                style={{ width: `${(Math.max(0, x.vanzari) / max) * 100}%` }}
              />
              <div
                className="h-1.5 rounded bg-b4l-text-dim/30"
                style={{ width: `${(Math.max(0, x.vanzariAnTrecut ?? 0) / max) * 100}%` }}
              />
            </div>
            <span className="text-right tabular-nums">{lei(x.vanzari)}</span>
          </div>
        ))}
      </div>,
      'Bara groasă e perioada aleasă, cea subțire aceeași perioadă de anul trecut. Suplimentele au sezon, de aceea comparația e cu anul trecut, nu cu luna dinainte.',
    )
  }
  if (card === 'marja') {
    const max = Math.max(30, ...sortate.map((x) => Math.abs(x.marja ?? 0)))
    return cadru(
      `Marja înainte de agenți, față de pragul de ${prag}%`,
      <div className="grid gap-1.5">
        {sortate.map((x) => (
          <div
            key={x.cheie}
            className="grid grid-cols-[minmax(0,180px)_1fr_70px] items-center gap-2 text-[13px]"
          >
            <span className="truncate">{x.nume}</span>
            <div className="h-2.5 rounded bg-b4l-bg-soft">
              <div
                className={`h-2.5 rounded ${(x.marja ?? 0) < 0 ? 'bg-b4l-red/50' : (x.marja ?? 0) < prag ? 'bg-b4l-amber/60' : 'bg-b4l-blue/60'}`}
                style={{ width: `${(Math.abs(x.marja ?? 0) / max) * 100}%` }}
              />
            </div>
            <span
              className={`text-right tabular-nums ${(x.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}`}
            >
              {pct(x.marja)}
            </span>
          </div>
        ))}
      </div>,
    )
  }
  const sub = randuri.filter((x) => x.profit < 0).sort((a, b) => a.profit - b.profit)
  return cadru(
    'Sub zero',
    sub.length ? (
      <ul className="grid gap-1 text-[13px]">
        {sub.map((x) => (
          <li key={x.cheie} className="flex flex-wrap justify-between gap-2">
            <span>{x.nume}</span>
            <span className="text-b4l-text-muted">
              servicii {lei(x.servicii)} lei ({pct(x.serviciiPct)} din vânzări) · profit{' '}
              <Semnat n={x.profit} />
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-b4l-text-muted">Niciunul în perioada asta.</p>
    ),
    'De obicei cauza e aceeași: serviciile facturate de client au trecut de marja pe produse.',
  )
}

const coloane = {
  vanzari: `Facturile B2B emise de noi: ${ETICHETE_CIFRE.vanzariNete.detaliu}, după stornouri.`,
  cantitate: 'Bucăți vândute (stornoul scade). Reducerile și serviciile nu sunt bucăți.',
  cost: 'Prețul de intrare din ExpertAccounts × cantitatea, pe fiecare linie vândută.',
  servicii:
    'Facturile primite de la client clasificate marketing, bonus de volum sau raft. Se leagă de client după codul fiscal.',
  profit: 'Vânzări − cost marfă − servicii client.',
  dupaCosturi:
    'Profitul minus ce costă agentul (estimat): salariu + taxe, bonus, mașină, carburant. La agentul care răspunde de conturile naționale se adună și profitul lor. Costurile se completează în panoul agentului; carburantul vine singur din Fill&Go.',
  marja: 'Profit / vânzări.',
  var: 'Vânzările față de aceeași perioadă de anul trecut.',
}

function TabelAgenti({
  r,
  chip,
  setChip,
  onDeschide,
}: {
  r: Raport
  chip: ChipAgenti
  setChip: (c: ChipAgenti) => void
  onDeschide: (k: string) => void
}) {
  const sortare = useSortare<ColAgent>('vanzari', { nume: 'asc' })
  const ag = r.agenti.filter((a) => !a.national)
  const nationale = r.agenti.find((a) => a.national)
  const filtre: Record<ChipAgenti, (a: Agent) => boolean> = {
    toti: () => true,
    slab: (a) => a.marja != null && a.marja < r.pragMarja,
    cusub: (a) => a.subZero > 0,
    lipsa: (a) => a.vanzariFaraCost !== 0,
    clasif: (a) => a.deClasificatNr > 0,
  }
  const vizibili = sortare.ordoneaza(ag.filter(filtre[chip]), (a, k) =>
    k === 'var' ? (a.vanzariAnTrecut ? a.vanzari / a.vanzariAnTrecut : null) : a[k],
  )
  const max = Math.max(1, ...ag.map((a) => a.vanzari))
  const rand = (a: Agent) => (
    <tr
      key={a.cheie}
      tabIndex={0}
      onClick={() => onDeschide(a.cheie)}
      onKeyDown={(e) => e.key === 'Enter' && onDeschide(a.cheie)}
      className="cursor-pointer border-t border-b4l-border hover:bg-b4l-bg-soft"
    >
      <td className="px-2 py-2 font-medium">
        <div className="flex flex-wrap items-center gap-1.5">
          {numeAgent(a, r)}
          <Semne m={a} prag={r.pragServicii} />
        </div>
      </td>
      <td className="px-2 py-2 text-right tabular-nums">
        <div className="flex items-center justify-end gap-2">
          {lei(a.vanzari)}
          {/* Conturile naționale sunt grup separat: n-au bară pe scara agenților (#2512). */}
          <BaraVanzari v={a.national ? null : a.vanzari} max={max} />
        </div>
      </td>
      <td className="px-2 py-2 text-right tabular-nums">{lei(a.cantitate)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{lei(a.cost)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{a.servicii ? lei(a.servicii) : '—'}</td>
      <td className="px-2 py-2 text-right">
        <Explicatie
          text={`${lei(a.vanzari)} − ${lei(a.cost)} − ${lei(a.servicii)} = ${a.profit < 0 ? '−' : ''}${lei(Math.abs(a.profit))} lei`}
        >
          <Semnat n={a.profit} />
        </Explicatie>
      </td>
      <td className="px-2 py-2 text-right">
        {a.costuri && a.dupaCosturi != null ? (
          <Explicatie
            text={`${a.profit < 0 ? '−' : ''}${lei(Math.abs(a.profit))}${a.profitNationale != null ? ` ${a.profitNationale < 0 ? '−' : '+'} ${lei(Math.abs(a.profitNationale))} profit conturi naționale` : ''} − ${lei(a.costuri.total)} costuri agent (salariu + taxe ${lei(a.costuri.salariuTaxe)}, bonus ${lei(a.costuri.bonus)}, mașină ${lei(a.costuri.auto)}, carburant ${lei(a.costuri.carburant)}) = ${a.dupaCosturi < 0 ? '−' : ''}${lei(Math.abs(a.dupaCosturi))} lei${a.costuri.luniLipsa.length ? ` — fără salariu completat: ${a.costuri.luniLipsa.map(etLuna).join(', ')}` : ''}`}
          >
            <span className="inline-flex items-center gap-1">
              <Semnat n={a.dupaCosturi} />
              {a.costuri.luniLipsa.length > 0 && <Steluta />}
            </span>
          </Explicatie>
        ) : (
          <span className="text-b4l-text-dim">—</span>
        )}
      </td>
      <td
        className={`px-2 py-2 text-right tabular-nums ${(a.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}`}
      >
        {pct(a.marja)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums">
        {a.clienti}
        {a.subZero > 0 && (
          <span className="ml-1.5">
            <Pastila ton="rosu">{a.subZero} sub zero</Pastila>
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        <Variatie acum={a.vanzari} trecut={a.vanzariAnTrecut} />
      </td>
    </tr>
  )
  return (
    <div className="grid gap-3">
      <ChipuriFiltru<ChipAgenti>
        ales={chip}
        onAlege={setChip}
        chipuri={[
          { cheie: 'toti', eticheta: 'Toți', numar: ag.length },
          {
            cheie: 'slab',
            eticheta: `Marjă sub ${r.pragMarja}%`,
            numar: ag.filter(filtre.slab).length,
          },
          {
            cheie: 'cusub',
            eticheta: 'Cu clienți sub zero',
            numar: ag.filter(filtre.cusub).length,
          },
          { cheie: 'lipsa', eticheta: 'Cost lipsă', numar: ag.filter(filtre.lipsa).length },
          { cheie: 'clasif', eticheta: 'De clasificat', numar: ag.filter(filtre.clasif).length },
        ]}
      />
      <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="text-xs text-b4l-text-muted">
            <tr>
              <CapSortabil className="!px-2" sortare={sortare} coloana="nume">
                Agent
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="vanzari"
                dreapta
                explicatie={coloane.vanzari}
              >
                Vânzări
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="cantitate"
                dreapta
                explicatie={coloane.cantitate}
              >
                Buc.
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="cost"
                dreapta
                explicatie={coloane.cost}
              >
                Cost marfă
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="servicii"
                dreapta
                explicatie={coloane.servicii}
              >
                Servicii client
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="profit"
                dreapta
                explicatie={coloane.profit}
              >
                Profit
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="dupaCosturi"
                dreapta
                explicatie={coloane.dupaCosturi}
              >
                După costuri agent (estimat)
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="marja"
                dreapta
                explicatie={coloane.marja}
              >
                Marjă
              </CapSortabil>
              <CapSortabil className="!px-2" sortare={sortare} coloana="clienti" dreapta>
                Clienți
              </CapSortabil>
              <CapSortabil
                className="!px-2"
                sortare={sortare}
                coloana="var"
                dreapta
                explicatie={coloane.var}
              >
                Față de an trecut
              </CapSortabil>
            </tr>
          </thead>
          <tbody>
            {vizibili.map(rand)}
            {!vizibili.length && (
              <tr>
                <td colSpan={10} className="px-2 py-6 text-center text-b4l-text-muted">
                  Niciun agent aici.
                </td>
              </tr>
            )}
            {nationale && (
              <>
                <tr>
                  <td
                    colSpan={10}
                    className="border-t border-b4l-border bg-b4l-bg-soft px-2 py-1.5 text-xs text-b4l-text-muted"
                  >
                    Grup separat: conturile naționale (lanțurile) nu intră în totalul și în media
                    agenților.
                  </td>
                </tr>
                {rand(nationale)}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Bara din coloana Vânzări: loc fix de 60px, umplerea plafonată la 100%. */
function BaraVanzari({ v, max }: { v: number | null; max: number }) {
  const fractie = v == null ? 0 : Math.min(1, Math.max(0, v) / max)
  return (
    <span className="inline-block w-[60px] shrink-0">
      {v != null && (
        <span className="block h-2 rounded bg-b4l-blue/40" style={{ width: `${fractie * 100}%` }} />
      )}
    </span>
  )
}

function TabelClienti({
  r,
  clienti,
  chip,
  setChip,
  onDeschide,
}: {
  r: Raport
  clienti: Client[]
  chip: ChipClienti
  setChip: (c: ChipClienti) => void
  onDeschide: (k: string) => void
}) {
  const sortare = useSortare<ColClient>('vanzari', { nume: 'asc' })
  const filtre: Record<ChipClienti, (c: Client) => boolean> = {
    toti: () => true,
    sub: (c) => c.profit < 0,
    slab: (c) => c.marja != null && c.marja < r.pragMarja,
    serv: (c) => c.serviciiPct != null && c.serviciiPct > r.pragServicii,
    lipsa: (c) => c.vanzariFaraCost !== 0,
    clasif: (c) => c.deClasificatNr > 0,
  }
  const vizibili = sortare.ordoneaza(clienti.filter(filtre[chip]), (c, k) =>
    k === 'var' ? (c.vanzariAnTrecut ? c.vanzari / c.vanzariAnTrecut : null) : c[k],
  )
  const max = Math.max(1, ...clienti.map((c) => c.vanzari))
  return (
    <div className="grid gap-3">
      <ChipuriFiltru<ChipClienti>
        ales={chip}
        onAlege={setChip}
        chipuri={[
          { cheie: 'toti', eticheta: 'Toți', numar: clienti.length },
          { cheie: 'sub', eticheta: 'Sub zero', numar: clienti.filter(filtre.sub).length },
          {
            cheie: 'slab',
            eticheta: `Marjă sub ${r.pragMarja}%`,
            numar: clienti.filter(filtre.slab).length,
          },
          {
            cheie: 'serv',
            eticheta: `Servicii peste ${r.pragServicii}%`,
            numar: clienti.filter(filtre.serv).length,
          },
          { cheie: 'lipsa', eticheta: 'Cost lipsă', numar: clienti.filter(filtre.lipsa).length },
          {
            cheie: 'clasif',
            eticheta: 'De clasificat',
            numar: clienti.filter(filtre.clasif).length,
          },
        ]}
      />
      <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="text-xs text-b4l-text-muted">
            <tr>
              <CapSortabil sortare={sortare} coloana="nume">
                Client
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="vanzari" dreapta explicatie={coloane.vanzari}>
                Vânzări
              </CapSortabil>
              <CapSortabil
                sortare={sortare}
                coloana="cantitate"
                dreapta
                explicatie={coloane.cantitate}
              >
                Buc.
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="cost" dreapta explicatie={coloane.cost}>
                Cost marfă
              </CapSortabil>
              <CapSortabil
                sortare={sortare}
                coloana="servicii"
                dreapta
                explicatie={coloane.servicii}
              >
                Servicii client
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="profit" dreapta explicatie={coloane.profit}>
                Profit
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="marja" dreapta explicatie={coloane.marja}>
                Marjă
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="var" dreapta explicatie={coloane.var}>
                Față de an trecut
              </CapSortabil>
            </tr>
          </thead>
          <tbody>
            {vizibili.map((c) => (
              <tr
                key={c.cheie}
                tabIndex={0}
                onClick={() => onDeschide(c.cheie)}
                onKeyDown={(e) => e.key === 'Enter' && onDeschide(c.cheie)}
                className="cursor-pointer border-t border-b4l-border hover:bg-b4l-bg-soft"
              >
                <td className="px-3 py-2 font-medium">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {c.nume}
                    {c.id == null && (
                      <Explicatie text="Partenerul din factură nu e găsit în registrul de clienți din CRM.">
                        <Pastila ton="gri">neînregistrat</Pastila>
                      </Explicatie>
                    )}
                    <Semne m={c} prag={r.pragServicii} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <div className="flex items-center justify-end gap-2">
                    {lei(c.vanzari)}
                    <BaraVanzari v={c.vanzari} max={max} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{lei(c.cantitate)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{lei(c.cost)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {c.servicii ? lei(c.servicii) : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <Explicatie
                    text={`${lei(c.vanzari)} − ${lei(c.cost)} − ${lei(c.servicii)} = ${c.profit < 0 ? '−' : ''}${lei(Math.abs(c.profit))} lei`}
                  >
                    <Semnat n={c.profit} />
                  </Explicatie>
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${(c.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}`}
                >
                  {pct(c.marja)}
                </td>
                <td className="px-3 py-2 text-right">
                  <Variatie acum={c.vanzari} trecut={c.vanzariAnTrecut} />
                </td>
              </tr>
            ))}
            {!vizibili.length && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-b4l-text-muted">
                  Niciun client aici.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Mini({ m }: { m: Metrici }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {[
        [
          'Vânzări',
          <span key="v" className="tabular-nums">
            {lei(m.vanzari)}
          </span>,
        ],
        ['Profit', <Semnat key="p" n={m.profit} />],
        [
          'Marjă',
          <span key="m" className={(m.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}>
            {pct(m.marja)}
          </span>,
        ],
      ].map(([e, v]) => (
        <div key={String(e)} className="rounded-lg bg-b4l-bg-soft px-3 py-2">
          <div className="text-[11px] uppercase tracking-wider text-b4l-text-dim">{e}</div>
          <div className="text-[15px] font-semibold">{v}</div>
        </div>
      ))}
    </div>
  )
}

function PanouAgent({
  r,
  cheie,
  onInchide,
  onClienti,
}: {
  r: Raport
  cheie: string
  onInchide: () => void
  onClienti: (k: string) => void
}) {
  const a = r.agenti.find((x) => x.cheie === cheie)
  if (!a) return null
  const ai = r.clienti.filter((c) => c.agentCheie === cheie).sort((x, y) => y.profit - x.profit)
  const capete = ai.length > 6 ? [...ai.slice(0, 3), ...ai.slice(-3)] : ai
  return (
    <PanouLateral
      deschis
      onInchide={onInchide}
      titlu={numeAgent(a, r)}
      subtitlu={<Pastila ton="gri">{a.clienti} clienți activi</Pastila>}
    >
      <Mini m={a} />
      <SectiunePanou titlu="Cum se ajunge la profit">
        <Cascada m={a} costuri={a.costuri} nationale={a.profitNationale} />
        {a.profitNationale != null && (
          <p className="mt-2 text-xs text-b4l-text-muted">
            Conturile naționale (lanțurile) sunt ale lui {a.nume}: profitul lor intră în ce rămâne
            după agent, dar nu și în totalul și media echipei, unde stau separat.
          </p>
        )}
        {a.costuri && a.costuri.luniLipsa.length > 0 && (
          <p className="mt-2 text-xs text-b4l-amber-dark">
            Fără salariu completat: {a.costuri.luniLipsa.map(etLuna).join(', ')}. Pentru lunile
            astea, ce rămâne după agent e mai mare decât în realitate.
          </p>
        )}
        {a.costuri && a.costuri.masini.length > 0 && (
          <p className="mt-1 text-xs text-b4l-text-muted">
            Carburant din Fill&amp;Go: {a.costuri.masini.join(', ')} (fără TVA, plus TVA-ul
            nedeductibil).
          </p>
        )}
      </SectiunePanou>
      {a.costuri && <CosturiAgent agent={a.cheie} an={Number(r.interval.pana.slice(0, 4))} />}
      <SectiunePanou titlu={ai.length > 6 ? 'Cei mai profitabili și cei mai slabi' : 'Clienții'}>
        <table className="w-full text-[13px]">
          <tbody>
            {capete.map((c) => (
              <tr key={c.cheie} className="border-t border-b4l-border">
                <td className="py-1 pr-2">{c.nume}</td>
                <td className="py-1 text-right">
                  <Semnat n={c.profit} />
                </td>
                <td className="py-1 pl-2 text-right tabular-nums">{pct(c.marja)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectiunePanou>
      <button
        type="button"
        onClick={() => onClienti(a.cheie)}
        className="justify-self-start rounded-md bg-b4l-blue px-3 py-1.5 text-sm font-medium text-b4l-pe-accent"
      >
        Vezi clienții lui {a.national ? '' : a.nume.split(' ')[0]} →
      </button>
    </PanouLateral>
  )
}

const sumaSauNull = (v: string): number | null => {
  const t = v.replace(/\s/g, '').replace(',', '.')
  if (t === '') return null
  const x = Number(t)
  return Number.isFinite(x) && x >= 0 ? Math.round(x * 100) / 100 : null
}
type CampCost = 'salariuTaxe' | 'bonus' | 'auto' | 'carburant'
const CAMPURI_COST: [CampCost, string][] = [
  ['salariuTaxe', 'Salariu + taxe'],
  ['bonus', 'Bonus'],
  ['auto', 'Mașină'],
  ['carburant', 'Carburant'],
]

/** Costurile agentului pe lunile anului: completate din statul de plată, carburantul din Fill&Go. */
function CosturiAgent({ agent, an }: { agent: string; an: number }) {
  const poate = useAuthStore(selectHasPermission('vanzari.profitabilitate:write'))
  const qc = useQueryClient()
  const q = useQuery<{ items: LunaCost[] }>({
    queryKey: ['profitabilitate-costuri', agent, an],
    queryFn: async () =>
      (
        await api.get<{ items: LunaCost[] }>('/profitabilitate/costuri-agent', {
          params: { agent, an },
        })
      ).data,
  })
  const [ciorna, setCiorna] = useState<Record<string, Partial<Record<CampCost, string>>>>({})
  const salveaza = useMutation({
    mutationFn: async (l: LunaCost) => {
      const c = ciorna[l.luna] ?? {}
      const val = (k: CampCost) => {
        const scris = c[k]
        return scris !== undefined ? sumaSauNull(scris) : l[k]
      }
      return api.put('/profitabilitate/costuri-agent', {
        agent,
        luna: l.luna,
        salariuTaxe: val('salariuTaxe'),
        bonus: val('bonus'),
        auto: val('auto'),
        carburant: val('carburant'),
      })
    },
    onSuccess: (_d, l) => {
      toast.success(`Costurile pe ${etLuna(l.luna)} s-au salvat.`)
      setCiorna((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== l.luna)))
      void qc.invalidateQueries({ queryKey: ['profitabilitate'] })
      void qc.invalidateQueries({ queryKey: ['profitabilitate-costuri', agent] })
    },
    onError: () => toast.error('Costurile nu s-au salvat. Verifică sumele.'),
  })
  const luni = q.data?.items ?? []
  return (
    <SectiunePanou titlu={`Costuri agent pe ${an} (${ETICHETE_CIFRE.costuriAgent.detaliu})`}>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-b4l-text-muted">
              <tr>
                <th className="py-1 text-left font-medium">Luna</th>
                {CAMPURI_COST.map(([k, e]) => (
                  <th key={k} className="py-1 text-right font-medium">
                    {e}
                  </th>
                ))}
                {poate && <th />}
              </tr>
            </thead>
            <tbody>
              {luni.map((l) => {
                const c = ciorna[l.luna]
                return (
                  <tr key={l.luna} className="border-t border-b4l-border">
                    <td className="whitespace-nowrap py-1 pr-1">{etLuna(l.luna)}</td>
                    {CAMPURI_COST.map(([k, e]) => {
                      const sugestie =
                        k === 'carburant' && l.carburantFillGo ? lei(l.carburantFillGo) : ''
                      const v = l[k]
                      const scris = c?.[k]
                      return (
                        <td key={k} className="py-0.5 pl-1 text-right">
                          {poate ? (
                            <input
                              inputMode="decimal"
                              aria-label={`${e}, ${etLuna(l.luna)}`}
                              value={scris !== undefined ? scris : v == null ? '' : String(v)}
                              placeholder={sugestie}
                              onChange={(ev) =>
                                setCiorna((x) => ({
                                  ...x,
                                  [l.luna]: { ...x[l.luna], [k]: ev.target.value },
                                }))
                              }
                              className="w-[72px] rounded border border-b4l-border bg-b4l-surface px-1 py-0.5 text-right tabular-nums placeholder:text-b4l-text-dim"
                            />
                          ) : (
                            <span className="tabular-nums">
                              {v != null ? lei(v) : sugestie || '—'}
                            </span>
                          )}
                        </td>
                      )
                    })}
                    {poate && (
                      <td className="py-0.5 pl-1">
                        {c && (
                          <button
                            type="button"
                            disabled={salveaza.isPending}
                            onClick={() => salveaza.mutate(l)}
                            className="rounded bg-b4l-blue px-1.5 py-0.5 text-[11px] font-medium text-b4l-pe-accent disabled:opacity-50"
                          >
                            Salvează
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-b4l-text-muted">
            Costul firmei, fără TVA. Salariu + taxe = brutul fără bonus + CAM; bonusul are și el
            CAM-ul lui. Carburantul gol se ia din Fill&amp;Go (cifra gri); o sumă scrisă îl
            înlocuiește.
          </p>
        </div>
      )}
    </SectiunePanou>
  )
}

type FilaClient = 'produse' | 'facturi' | 'primite'

function PanouClient({
  r,
  cheie,
  perioada,
  pas,
  onInchide,
}: {
  r: Raport
  cheie: string
  perioada: Perioada
  pas: number
  onInchide: () => void
}) {
  const c = r.clienti.find((x) => x.cheie === cheie)
  const [fila, setFila] = useState<FilaClient>('produse')
  const poateClasifica = useAuthStore(selectHasPermission('vanzari.profitabilitate:write'))
  const qc = useQueryClient()
  const d = useQuery<Detaliu>({
    queryKey: ['profitabilitate-client', perioada, pas, cheie],
    queryFn: async () =>
      (
        await api.get<Detaliu>('/profitabilitate/client', {
          params: { perioada, pas, cheie },
          timeout: TIMP_CALCUL,
        })
      ).data,
  })
  const clasifica = useMutation({
    mutationFn: async ({ id, tip }: { id: number; tip: TipFactura | null }) =>
      api.put(`/profitabilitate/facturi-primite/${id}/clasificare`, { tip }),
    onSuccess: () => {
      toast.success('Clasificarea s-a salvat. Profitul s-a recalculat.')
      void qc.invalidateQueries({ queryKey: ['profitabilitate'] })
      void qc.invalidateQueries({ queryKey: ['profitabilitate-client'] })
    },
    onError: () => toast.error('Clasificarea nu s-a salvat. Încearcă din nou.'),
  })
  const salveazaPerioada = useMutation({
    mutationFn: async ({
      id,
      perioada,
    }: {
      id: number
      perioada: { de: string; pana: string } | null
    }) => api.put(`/profitabilitate/facturi-primite/${id}/perioada`, { perioada }),
    onSuccess: () => {
      toast.success('Perioada s-a salvat. Profitul s-a recalculat.')
      void qc.invalidateQueries({ queryKey: ['profitabilitate'] })
      void qc.invalidateQueries({ queryKey: ['profitabilitate-client'] })
    },
    onError: () => toast.error('Perioada nu s-a salvat. Încearcă din nou.'),
  })
  if (!c) return null
  const agent = r.agenti.find((a) => a.cheie === c.agentCheie)

  return (
    <PanouLateral
      deschis
      onInchide={onInchide}
      titlu={c.nume}
      subtitlu={
        <>
          <Pastila ton="gri">{agent?.nume ?? c.agentCheie}</Pastila>
          <Pastila ton="gri">{r.eticheta}</Pastila>
        </>
      }
    >
      <Mini m={c} />
      <SectiunePanou titlu="Cum s-a calculat">
        <Cascada m={c} />
      </SectiunePanou>
      {c.profit < 0 && c.servicii > 0 && (
        <p className="rounded-lg bg-b4l-bg-soft px-3 py-2 text-[13px]">
          Serviciile facturate de client ({lei(c.servicii)} lei, adică {pct(c.serviciiPct)} din
          vânzări) au trecut de marja pe produse ({lei(c.vanzari - c.cost)} lei).
        </p>
      )}
      {c.vanzariFaraCost !== 0 && (
        <p className="rounded-lg bg-b4l-amber-light px-3 py-2 text-[13px] text-b4l-amber-dark">
          Pentru {lei(c.vanzariFaraCost)} lei din vânzări n-am găsit costul de intrare. Profitul
          poate fi mai mic cu cel mult atât.
        </p>
      )}

      <ChipuriFiltru<FilaClient>
        ales={fila}
        onAlege={setFila}
        eticheta="Detalii"
        chipuri={[
          { cheie: 'produse', eticheta: 'Produse', numar: d.data?.produse.length },
          { cheie: 'facturi', eticheta: 'Facturi vânzare', numar: d.data?.facturi.length },
          { cheie: 'primite', eticheta: 'Facturi primite', numar: d.data?.facturiPrimite.length },
        ]}
      />
      {d.isLoading && <Spinner />}
      {d.isError && <p className="text-sm text-b4l-red-dark">Detaliile nu s-au putut citi.</p>}
      {d.data && fila === 'produse' && (
        <table className="w-full text-[12.5px]">
          <thead className="text-b4l-text-muted">
            <tr>
              <th className="py-1 text-left font-medium">Produs</th>
              <th className="py-1 text-right font-medium">Buc.</th>
              <th className="py-1 text-right font-medium">Vânzări</th>
              <th className="py-1 text-right font-medium">Cost</th>
              <th className="py-1 text-right font-medium">Marjă</th>
            </tr>
          </thead>
          <tbody>
            {d.data.produse.map((p) => (
              <tr key={p.sku} className="border-t border-b4l-border">
                <td className="py-1 pr-2">
                  <div>{p.denumire}</div>
                  <div className="text-[11px] text-b4l-text-dim">{p.sku}</div>
                </td>
                <td className="py-1 text-right tabular-nums">{lei(p.cantitate)}</td>
                <td className="py-1 text-right tabular-nums">{lei(p.vanzari)}</td>
                <td className="py-1 text-right tabular-nums">
                  {p.cost == null ? (
                    <Pastila ton="galben">fără cost</Pastila>
                  ) : (
                    <Explicatie
                      text={`Sursa: ${p.surse.map((s) => ETICHETA_SURSA[s]).join('; ')}.`}
                    >
                      {lei(p.cost)}
                    </Explicatie>
                  )}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {p.cost == null || p.vanzari === 0
                    ? '—'
                    : pct(Math.round(((p.vanzari - p.cost) / p.vanzari) * 1000) / 10)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {d.data && fila === 'facturi' && (
        <table className="w-full text-[12.5px]">
          <tbody>
            {d.data.facturi.map((f) => (
              <tr key={f.numar} className="border-t border-b4l-border">
                <td className="py-1">
                  {f.numar} {f.storno && <Pastila ton="gri">storno</Pastila>}
                </td>
                <td className="py-1 text-b4l-text-muted">{dmy(f.data)}</td>
                <td className="py-1 text-right">
                  <Semnat n={f.valoare} />
                </td>
              </tr>
            ))}
            {!d.data.facturi.length && (
              <tr>
                <td className="py-2 text-b4l-text-muted">Nicio factură de vânzare în perioadă.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {d.data && fila === 'primite' && (
        <div className="grid gap-2">
          {d.data.facturiPrimite.map((f) => (
            <div
              key={f.id}
              className="grid gap-1 rounded-lg border border-b4l-border px-3 py-2 text-[12.5px]"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-medium">{f.numar ?? 'fără număr'}</span>
                <span className="tabular-nums">
                  {f.valoare === f.valoareTotala ? (
                    <>{lei(f.valoare)} lei fără TVA</>
                  ) : (
                    <Explicatie
                      text={`Factura e de ${lei(f.valoareTotala)} lei fără TVA, împărțită egal pe ${f.luni.length} luni. În perioada aleasă intră doar partea lunilor ei.`}
                    >
                      {lei(f.valoare)} lei în perioadă, din {lei(f.valoareTotala)}
                    </Explicatie>
                  )}
                </span>
              </div>
              <div className="text-b4l-text-muted">
                emisă {dmy(f.data)} · {f.emitent ?? '—'}
              </div>
              <PerioadaFacturii
                key={`${f.id}-${f.luni.join(',')}`}
                id={f.id}
                luni={f.luni}
                sursa={f.sursaPerioada}
                poateSchimba={poateClasifica}
                ocupat={salveazaPerioada.isPending}
                onSalveaza={(p) => salveazaPerioada.mutate({ id: f.id, perioada: p })}
              />
              {poateClasifica ? (
                <label className="flex items-center gap-2">
                  <span className="text-b4l-text-muted">Ce este:</span>
                  <select
                    id={`clasificare-${f.id}`}
                    value={f.tip ?? ''}
                    disabled={clasifica.isPending}
                    onChange={(e) =>
                      clasifica.mutate({
                        id: f.id,
                        tip: (e.target.value || null) as TipFactura | null,
                      })
                    }
                    className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1"
                  >
                    <option value="">De clasificat (nu scade profitul)</option>
                    {(Object.keys(ETICHETA_TIP) as TipFactura[]).map((t) => (
                      <option key={t} value={t}>
                        {ETICHETA_TIP[t]}
                      </option>
                    ))}
                  </select>
                  {f.sursaTip === 'automat' && <PastilaAutomat />}
                </label>
              ) : (
                <span className="flex items-center gap-2">
                  {f.tip ? ETICHETA_TIP[f.tip] : 'De clasificat'}
                  {f.sursaTip === 'automat' && <PastilaAutomat />}
                </span>
              )}
            </div>
          ))}
          {!d.data.facturiPrimite.length && (
            <p className="text-b4l-text-muted">
              Nicio factură primită de la acest client în perioadă.
            </p>
          )}
          <p className="text-[11.5px] text-b4l-text-dim">
            Doar marketingul, bonusul de volum și raftul scad profitul. „Altele” (de exemplu marfă
            cumpărată de la un distribuitor) nu. Costul intră în luna la care se referă factura, nu
            în luna emiterii; o factură pe mai multe luni se împarte egal pe ele.
          </p>
        </div>
      )}
    </PanouLateral>
  )
}
